import { HttpClient } from '@angular/common/http';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { API_BASE_URL } from './api-config';
import { AboutPageComponent } from './components/about-page/about-page.component';
import { BlogDetailPageComponent } from './components/blog-detail-page/blog-detail-page.component';
import { ContactPageComponent } from './components/contact-page/contact-page.component';
import { ContentPageComponent } from './components/content-page/content-page.component';
import { DevelopmentsPageComponent } from './components/developments-page/developments-page.component';
import { HomePageComponent } from './components/home-page/home-page.component';
import { InquiryModalComponent } from './components/inquiry-modal/inquiry-modal.component';
import { InsightsPageComponent } from './components/insights-page/insights-page.component';
import { LegalPageComponent } from './components/legal-page/legal-page.component';
import { ProjectPageComponent } from './components/project-page/project-page.component';
import { SiteFooterComponent } from './components/site-footer/site-footer.component';
import { SiteHeaderComponent } from './components/site-header/site-header.component';
import {
  ContentTab,
  InquiryForm,
  Project,
  ProjectSlug,
  PublicBlogPost,
  PublicPropertyProject,
  PublicYouTubeVideo,
  SitePage,
} from './project-data';

interface PropertyImageFallback {
  cover: string;
  logo: string;
}

const PROPERTY_IMAGE_FALLBACKS: Record<string, PropertyImageFallback> = {
  canopy: {
    cover: '/property-covers/canopy.webp',
    logo: '/project-logos/canopy-logo.webp',
  },
  cub: {
    cover: '/property-covers/cub.png',
    logo: '/project-logos/cub-logo.webp',
  },
  canvas: {
    cover: '/property-covers/canvas.webp',
    logo: '/project-logos/canvas-logo.webp',
  },
};

const DEFAULT_PROPERTY_IMAGE_FALLBACK = PROPERTY_IMAGE_FALLBACKS['canopy'];

@Component({
  selector: 'app-root',
  imports: [
    AboutPageComponent,
    BlogDetailPageComponent,
    ContactPageComponent,
    ContentPageComponent,
    DevelopmentsPageComponent,
    HomePageComponent,
    InquiryModalComponent,
    InsightsPageComponent,
    LegalPageComponent,
    ProjectPageComponent,
    SiteFooterComponent,
    SiteHeaderComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements OnInit {
  private readonly http = inject(HttpClient);

  protected readonly activePage = signal<SitePage>('home');
  protected readonly contentTab = signal<ContentTab>('events');
  protected readonly projects = signal<Project[]>([]);
  protected readonly selectedProject = signal<Project | null>(null);
  protected readonly propertiesLoading = signal(true);
  protected readonly propertiesError = signal(false);
  protected readonly blogPosts = signal<PublicBlogPost[]>([]);
  protected readonly blogLoading = signal(true);
  protected readonly blogError = signal(false);
  protected readonly youtubeVideos = signal<PublicYouTubeVideo[]>([]);
  protected readonly youtubeLoading = signal(true);
  protected readonly youtubeError = signal(false);
  protected readonly selectedBlogPost = signal<PublicBlogPost | null>(null);
  protected readonly blogDetailLoading = signal(false);
  protected readonly blogDetailError = signal(false);
  protected readonly homepageProjects = computed(() => this.projects().slice(0, 3));
  protected readonly homepageBlogPosts = computed(() => this.blogPosts().slice(0, 3));
  protected readonly inquiryOpen = signal(false);
  protected readonly messageSending = signal(false);
  protected readonly messageSent = signal(false);
  protected readonly messageError = signal(false);
  protected readonly inquiry = signal<InquiryForm>({
    name: '',
    phone: '',
    email: '',
    property: '',
    message: '',
  });

  ngOnInit(): void {
    this.loadProperties();
    this.loadBlogPosts();
    this.loadYoutubeVideos();
  }

  protected showPage(page: SitePage): void {
    this.activePage.set(page);
    this.scrollToTop();
  }

  protected openProject(slug: ProjectSlug): void {
    const project = this.projects().find((item) => item.slug === slug);
    if (!project) {
      return;
    }

    this.selectedProject.set(project);
    this.activePage.set('project');
    this.scrollToTop();
  }

  protected openBlogPost(post: PublicBlogPost): void {
    this.selectedBlogPost.set(post);
    this.blogDetailLoading.set(true);
    this.blogDetailError.set(false);
    this.activePage.set('blog-detail');
    this.scrollToTop();

    this.http
      .get<PublicBlogPost>(`${API_BASE_URL}/api/public/blog-posts/${post.slug}/`)
      .subscribe({
        next: (latestPost) => {
          this.selectedBlogPost.set(latestPost);
          this.blogDetailLoading.set(false);
        },
        error: () => {
          this.blogDetailError.set(true);
          this.blogDetailLoading.set(false);
        },
      });
  }

  protected showContentTab(tab: ContentTab): void {
    this.contentTab.set(tab);
  }

  protected showHomeSection(sectionId: string): void {
    this.activePage.set('home');
    window.setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  protected openInquiry(event?: Event, property = ''): void {
    event?.preventDefault();
    this.messageSending.set(false);
    this.messageSent.set(false);
    this.messageError.set(false);
    this.inquiry.update((current) => ({ ...current, property }));
    this.inquiryOpen.set(true);
  }

  protected closeInquiry(): void {
    this.inquiryOpen.set(false);
  }

  protected updateInquiry(field: keyof InquiryForm, value: string): void {
    this.messageSent.set(false);
    this.messageError.set(false);
    this.inquiry.update((current) => ({ ...current, [field]: value }));
  }

  protected sendInquiry(): void {
    const inquiry = this.inquiry();
    const payload: InquiryForm = {
      name: inquiry.name.trim(),
      phone: inquiry.phone.trim(),
      email: inquiry.email.trim(),
      property: inquiry.property.trim(),
      message: inquiry.message.trim(),
    };

    if (this.messageSending()) {
      return;
    }

    this.messageSending.set(true);
    this.messageSent.set(false);
    this.messageError.set(false);

    this.http.post(`${API_BASE_URL}/api/public/inquiries/`, payload).subscribe({
      next: () => {
        this.messageSending.set(false);
        this.messageSent.set(true);
      },
      error: () => {
        this.messageSending.set(false);
        this.messageError.set(true);
      },
    });
  }

  private scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private loadBlogPosts(): void {
    this.blogLoading.set(true);
    this.blogError.set(false);

    this.http.get<PublicBlogPost[]>(`${API_BASE_URL}/api/public/blog-posts/`).subscribe({
      next: (posts) => {
        this.blogPosts.set(posts);
        this.blogLoading.set(false);
      },
      error: () => {
        this.blogPosts.set([]);
        this.blogError.set(true);
        this.blogLoading.set(false);
      },
    });
  }

  private loadProperties(): void {
    this.propertiesLoading.set(true);
    this.propertiesError.set(false);

    this.http
      .get<PublicPropertyProject[]>(`${API_BASE_URL}/api/public/properties/`)
      .subscribe({
        next: (properties) => {
          this.projects.set(properties.map((property) => this.propertyToProject(property)));
          this.propertiesLoading.set(false);
        },
        error: () => {
          this.projects.set([]);
          this.propertiesError.set(true);
          this.propertiesLoading.set(false);
        },
      });
  }

  private loadYoutubeVideos(): void {
    this.youtubeLoading.set(true);
    this.youtubeError.set(false);

    this.http.get<PublicYouTubeVideo[]>(`${API_BASE_URL}/api/public/youtube-videos/`).subscribe({
      next: (videos) => {
        this.youtubeVideos.set(videos);
        this.youtubeLoading.set(false);
      },
      error: () => {
        this.youtubeVideos.set([]);
        this.youtubeError.set(true);
        this.youtubeLoading.set(false);
      },
    });
  }

  private propertyToProject(property: PublicPropertyProject): Project {
    const fallbackText = property.meta_description || property.name;
    const fallbackImages = this.propertyImageFallback(property);
    const coverImage = this.apiAssetUrl(property.cover_image_url);
    const logoImage = this.apiAssetUrl(property.logo_image_url);
    const projectImage = coverImage || fallbackImages.cover;

    return {
      id: property.id,
      slug: `property-${property.id}`,
      name: property.name,
      location: '',
      iframeUrl: property.iframe_project_url,
      price: property.pricing,
      type: '',
      size: '',
      heroImage: projectImage,
      heroFallbackImage: fallbackImages.cover,
      logoImage: logoImage || fallbackImages.logo,
      logoFallbackImage: fallbackImages.logo,
      videoPoster: projectImage,
      summary: fallbackText,
      intro: fallbackText,
      stats: [property.pricing, this.propertyStatusLabel(property.status)],
      amenities: [],
      amenityImages: [projectImage],
      interiors: [projectImage],
    };
  }

  private apiAssetUrl(source: string | null | undefined): string {
    const trimmedSource = (source ?? '').trim();

    if (!trimmedSource) {
      return '';
    }

    if (
      /^(https?:)?\/\//i.test(trimmedSource) ||
      trimmedSource.startsWith('data:') ||
      trimmedSource.startsWith('blob:')
    ) {
      return trimmedSource;
    }

    if (trimmedSource.startsWith('/')) {
      return `${API_BASE_URL}${trimmedSource}`;
    }

    return `${API_BASE_URL}/${trimmedSource}`;
  }

  private propertyImageFallback(property: PublicPropertyProject): PropertyImageFallback {
    const name = property.name.toLowerCase();

    if (name.includes('cub')) {
      return PROPERTY_IMAGE_FALLBACKS['cub'];
    }

    if (name.includes('canvas')) {
      return PROPERTY_IMAGE_FALLBACKS['canvas'];
    }

    if (name.includes('canopy')) {
      return PROPERTY_IMAGE_FALLBACKS['canopy'];
    }

    return DEFAULT_PROPERTY_IMAGE_FALLBACK;
  }

  private propertyStatusLabel(status: PublicPropertyProject['status']): string {
    return status
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
}
